import { Component, computed, signal } from '@angular/core'

import { FormsModule } from '@angular/forms'
import { ChargeMethod, EntityRef, EntityRefWithUID } from 'src/app/shared/models/referenceData'
import { DateRangeFilterStateValue } from 'src/app/shared/services/filter/date-range-filter'
import { DateRangeFilterPickerComponent } from '../date-range-filter-picker/date-range-filter-picker.component'
import { EntitySelectorComponent } from '../entity-selector/entity-selector.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { ModalBodyComponent } from '../modals/modal-body/modal-body.component'
import { ModalFooterComponent } from '../modals/modal-footer/modal-footer.component'
import { ModalHeaderComponent } from '../modals/modal-header/modal-header.component'
import { TBillSPChargeCreateRequest } from '../settlements/tbill-settlements.types'
import { NumberInputComponent } from '../number-input/number-input.component'
import { TextInputComponent } from '../text-input/text-input.component'

type ModalState = Partial<{ 
  effective_date: DateRangeFilterStateValue, 
  termination_date: DateRangeFilterStateValue, 
  retailer: EntityRefWithUID, 
  charge_type: EntityRefWithUID,
  charge_method: EntityRefWithUID,
  charge_description_override: string | null
  charge_template_values: { key: string, value: number }[]
}>

@Component({
  imports: [
    FormsModule,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    LabeledPropertyComponent,
    DateRangeFilterPickerComponent,
    EntitySelectorComponent,
    NumberInputComponent,
    TextInputComponent,
  ],
  selector: 'app-create-tbill-sp-charge-modal',
  styles: '',
  template: `
    <div app-modal-header>Create SP Charge</div>

    <div app-modal-body>
      <div app-labeled-property label="Retailer">
        <div
          app-entity-selector
          [slim]="true"
          label="Retailer"
          [config]="{
              resource: 'retailers', 
              allowMultiple: false
          }"
          [value]="modalState().retailer"
          (valueChange)="updateRetailer($event)"
        ></div>
      </div>

      <div app-labeled-property label="Charge Type">
        <div
          app-entity-selector
          [slim]="true"
          label="Charge Type"
          [config]="{
              resource: 'tbillSPChargeTypes', 
              allowMultiple: false
          }"
          [value]="modalState().charge_type"
          (valueChange)="updateChargeType($event)"
        ></div>
      </div>

      <div app-labeled-property label="Effective Date">
        <div dateRangeFilterPicker
        [value]="modalState().effective_date"
        (valueChange)="updateEffectiveDate($event)"
        [showOperation]="false"
        [showRange]="false"
        [showTime]="false"
        [showSeconds]="false"
        [onlyMonth]="false"
        ></div>      
      </div>
      <div app-labeled-property label="Termination Date">
        <div dateRangeFilterPicker
        [value]="modalState().termination_date"
        (valueChange)="updateTerminationDate($event)"
        [showOperation]="false"
        [showRange]="false"
        [showTime]="false"
        [showSeconds]="false"
        [onlyMonth]="false"
        ></div>
      </div>

      <div app-labeled-property label="Charge Method">
        <div
          app-entity-selector
          [slim]="true"
          label="Charge Type"
          [config]="{
              resource: 'tbillSPChargeMethods', 
              allowMultiple: false
          }"
          [value]="modalState().charge_method"
          (valueChange)="updateChargeMethod($event)"
        ></div>
      </div>

      <div app-labeled-property label="Charge Description Override">
          <div app-text-input [value]="modalState().charge_description_override" (valueChange)="updateDescription($event)"></div>
      </div>
      
      @for(item of modalState().charge_template_values; track item.key) {
        <div app-labeled-property [label]="item.key">
          <div app-number-input [value]="item.value" (valueChange)="updateTemplateValue(item.key, $event)"></div>
        </div>
      }
    </div>

    <div app-modal-footer>
        <button
        class="action-btn primary"
        [disabled]="!canCreate()"
        (click)="create()"
        >
        Create
        </button>
        <button class="action-btn" (click)="onCancel()()">Cancel</button>
    </div>
    `,
})
export class CreateTBillSPChargeModalComponent {
  protected readonly modalState = signal<ModalState>({})
  protected readonly onCreate = signal<(req: TBillSPChargeCreateRequest) => void>(() => void 0)
  protected readonly onCancel = signal<() => void>(() => void 0)

  canCreate = computed(() => {
    return this.modalState().retailer?.entity_uid 
        && this.modalState().charge_type?.entity_uid
        && this.modalState().charge_method?.entity_uid
        && this.modalState().effective_date
        && this.modalState().termination_date
        && this.modalState().charge_template_values
  })

  init(overrides: Partial<{
    modalState: ModalState,
    onCreate: (req: TBillSPChargeCreateRequest) => void,
    onCancel: () => void
  }>) {
    this.modalState.update(v => overrides.modalState ?? v)
    this.onCreate.update(v => overrides.onCreate ?? v)
    this.onCancel.update(v => overrides.onCancel ?? v)
  }

  updateRetailer(retailer: EntityRef) {
    this.modalState.update(v => ({ ...v, retailer: retailer as EntityRefWithUID }))
  }
  updateChargeType(charge_type: EntityRef) {
    this.modalState.update(v => ({ ...v, charge_type: charge_type as EntityRefWithUID }))
  }
  updateEffectiveDate(effective_date: DateRangeFilterStateValue) {
    this.modalState.update(v => ({ ...v, effective_date }))
  }
  updateTerminationDate(termination_date: DateRangeFilterStateValue) {
    this.modalState.update(v => ({ ...v, termination_date }))
  }
  updateChargeMethod(charge_method: EntityRef) {
    const charge_template_values = [...(charge_method as ChargeMethod).charge_method_template
      .matchAll(/\$\{(\w+)}/g)]
      .map((m) => ({
        key: m[1], value: 0,
      }))
    this.modalState.update(v => ({ ...v, charge_method: charge_method as EntityRefWithUID, charge_template_values }))
  }
  updateDescription(val: string){
    this.modalState.update(v => ({ ...v, charge_description_override: val || null }))
  }
  updateTemplateValue(key: string, value: number) {
    this.modalState.update(v => ({
      ...v, 
      charge_template_values: (v.charge_template_values ?? []).map((tmpl) => tmpl.key === key ? { key, value } : tmpl),
    }))
  }

  create() {
    this.onCreate()({
      retailer_uid: this.modalState().retailer!.entity_uid,
      tbill_sp_charge_type_uid: this.modalState().charge_type!.entity_uid,
      effective_date: this.modalState().effective_date!.value as Date,
      termination_date: this.modalState().termination_date!.value as Date,
      charge_method_uid: this.modalState().charge_method!.entity_uid,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      charge_description_override: this.modalState().charge_description_override || null,
      charge_template_values: this.modalState().charge_template_values!
        .reduce((acc, next)=> ({ ...acc, [next.key]: next.value }), {} as Record<string, number>),
    })
  }
}
